import * as React from 'react';
import {
    Edit,
    SimpleForm,
    TextInput,
    useTranslate,
    useRecordContext,
    AutocompleteInput,
    required,
    FormDataConsumer,
    ArrayInput,
    SelectArrayInput,
    useDataProvider,
} from 'react-admin';
import { EditToolbar } from '../../common/EditToolbar';
import { BASE_URL, BOOLEAN, VIDEO_ENCODING, VIDEO_TYPES } from '../../common/constants';
import { InputAdornment } from '@mui/material';
import useAllowedQualities from '../../hooks/useAllowedQualities';
import { validateNotEmpty } from '../../common/VideoPlayer/util';
import FileUpload from '../../common/inputs/FileUpload';

export const getYouTubeEmbedCode = (data: any): string => {
    if (data.type !== 'YOUTUBE') return data.thumbnail || '';
    const url = data.type === 'YOUTUBE' ? `https://www.youtube.com/embed/${data.rawUrl}` : '';
    const regex =
        /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|embed\/|v=|v\/|.+\?v=)|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/;
    const match = url.match(regex);
    const code = match ? match[1] : '';
    return data.thumbnail || `https://i.ytimg.com/vi/${code}/hq720.jpg`;
};

const validateYouTubeId = (value: any) => {
    if (!value) return 'Required';
    let youtubeId = value;
    if (typeof value === 'string' && value.includes('youtube.com')) {
        const regex =
            /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|embed\/|v=|v\/|.+\?v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
        const match = value.match(regex);
        youtubeId = match ? match[1] : value;
    }

    youtubeId = youtubeId.replace(/^https:\/\/www\.youtube\.com\/embed\//, '');

    const idMatch = youtubeId.match(/^[\w-]{11}$/);
    return idMatch ? undefined : 'Invalid YouTube video ID (expected 11 characters)';
};

const VideoEdit = () => {
    const Qualities = useAllowedQualities();
    const dataProvider = useDataProvider();
    const record = useRecordContext();
    const [subjects, setSubjects] = React.useState<any[]>([]);
    const [loadingSubjects, setLoadingSubjects] = React.useState<boolean>(true);

    React.useEffect(() => {
        dataProvider
            .getList('video-subjects', {
                pagination: { page: 1, perPage: 100 },
                sort: { field: 'name', order: 'ASC' },
                filter: {},
            })
            .then(({ data }) => {
                let updatedSubjects = data;
                if (
                    record?.videoSubjectId &&
                    !data.some((s: any) => s.id === record.videoSubjectId)
                ) {
                    updatedSubjects = [...data, { id: record.videoSubjectId, name: 'Loading...' }];
                }
                setSubjects(updatedSubjects);
                setLoadingSubjects(false);
            })
            .catch(error => {
                console.error('Error fetching subjects:', error);
                setLoadingSubjects(false);
            });
    }, [dataProvider, record?.videoSubjectId]);

    const subjectChoices = subjects.map(s => ({ id: String(s.id), name: s.name }));

    return (
        <Edit
            mutationMode={'pessimistic'}
            title={<VideoEditTitle />}
            transform={(data: any) => ({
                ...data,
                rawUrl: data.rawUrl?.replace(/^https:\/\/www\.youtube\.com\/embed\//, ''),
                processedUrl:
                    data.type === 'YOUTUBE'
                        ? `https://www.youtube.com/embed/${data.rawUrl?.replace(
                            /^https:\/\/www\.youtube\.com\/embed\//,
                            '',
                        )}`
                        : data.processedUrl,
                thumbnail: getYouTubeEmbedCode(data),
            })}
        >
            <SimpleForm toolbar={<EditToolbar />}>
                <FileUpload type="VIDEO_THUMBNAIL" source={'thumbnail'} />
                <TextInput source="title" fullWidth validate={[required(), validateNotEmpty]} />
                <AutocompleteInput
                    source="videoSubjectId"
                    label="Subject"
                    fullWidth
                    validate={[required()]}
                    choices={subjectChoices}
                    optionText="name"
                    optionValue="id"
                    readOnly={loadingSubjects}
                />

                <AutocompleteInput
                    source="type"
                    label="Type"
                    fullWidth
                    validate={[required()]}
                    choices={VIDEO_TYPES}
                    readOnly
                />

                <FormDataConsumer>
                    {({ formData, ...rest }) => {
                        if (formData?.type === 'YOUTUBE') {
                            return (
                                <TextInput
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                https://www.youtube.com/embed/
                                            </InputAdornment>
                                        ),
                                    }}
                                    id="youtube-input"
                                    label="YouTube Embed Code"
                                    fullWidth
                                    validate={[required(), validateYouTubeId]}
                                    source="rawUrl"
                                    parse={v => v}
                                    format={v =>
                                        v?.replace(/^https:\/\/www\.youtube\.com\/embed\//, '')
                                    }
                                    {...rest}
                                />
                            );
                        } else if (formData?.type) {
                            return (
                                <>
                                    <AutocompleteInput
                                        source="isDownloadable"
                                        label="Downloadable"
                                        choices={BOOLEAN}
                                        disabled={formData?.type === 'YOUTUBE' ? true : false}
                                        validate={[required()]}
                                        fullWidth
                                    />
                                    <ArrayInput source="qualities" defaultValue={['360', '480']}>
                                        <SelectArrayInput
                                            label="Video Qualities"
                                            choices={Qualities}
                                            fullWidth
                                            readOnly
                                        />
                                    </ArrayInput>
                                    <ArrayInput source="encodings" defaultValue={['HLS', 'DASH']}>
                                        <SelectArrayInput
                                            label="Video Encodings"
                                            choices={VIDEO_ENCODING}
                                            fullWidth
                                        />
                                    </ArrayInput>
                                    <FileUpload
                                        type="video"
                                        meta={{
                                            encodings: formData.encodings,
                                            qualities: formData.qualities,
                                        }}
                                        required={true}
                                        source="rawUrl"
                                        accept="video/*"
                                        multiple={false}
                                    />
                                </>
                            );
                        }
                        return null;
                    }}
                </FormDataConsumer>
            </SimpleForm>
        </Edit>
    );
};

const VideoEditTitle = () => {
    const record = useRecordContext<any>();
    const translate = useTranslate();
    return record ? (
        <span>
            {translate('resources.videos.name', { smart_count: 1 })} &quot;{record.title}&quot;
        </span>
    ) : null;
};

export default VideoEdit;
